<template>
  <div>
    <app-cells v-if="reservation.reservation_status.code === 'booked_provisionally' && reservation.sales_status.code === 'book_provisionally'">
      <app-button
        v-if="!reservation.provisional_extension"
        @click.native="onRequestExtensionPopupOpen"
        theme="info"
      >
        Request provisional extension
      </app-button>
      <app-colored-notes
        v-if="reservation.provisional_extension"
        color="#35a4ea"
        :text="'You have requested provisional reservation extension. Wait for hotel answer.'"
        :indent="false"
      />
    </app-cells>
    <app-cells
      v-if="(reservation.reservation_status.code === 'booked' && reservation.hotel.voucher_required) || reservation.reservation_status.code === 'booked_provisionally'"
    >
      <app-form-group
        v-if="reservation.reservation_status.code === 'booked' && reservation.hotel.voucher_required"
        label="Voucher"
        class="form-group--no-indent"
      >
        <app-uploader
          v-model="reservation.voucher"
          class="custom-uploader--min-150"
          :extensions="['application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp', 'pdf']"
          :readonly="!is_general_manager && !is_reservations_department"
        />
      </app-form-group>
      <app-form-group v-if="reservation.reservation_status.code === 'booked_provisionally'" label="Deadline" class="form-group--no-indent">
        <app-date-picker
          v-model="reservation.reservation_deadline"
          placeholder="Deadline"
          :readonly="!is_general_manager && !is_reservations_department"
        />
      </app-form-group>
      <app-button
        @click.native="onReservationPatch"
        ref="save_other_options"
        style="align-self: flex-end;"
      >
        Save
      </app-button>
    </app-cells>
    <app-form-group label="Reservation notes" label-for="reservation_notes" style="max-width: 1080px">
      <app-textarea
        @change.native="onPatchReservationNotes"
        v-model="reservation.reservation_notes"
        rows="5"
        placeholder="Write a note"
        id="reservation_notes"
      />
    </app-form-group>
    <modal
      name="request_extension_modal"
      :width="420"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('request_extension_modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Request extension</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckRequestExtensionForm">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="form_request_extension.date"
                placeholder="Date"
                :error="$v.form_request_extension.date.$error"
              />
              <template #error>
                <div v-if="$v.form_request_extension.date.$dirty && !$v.form_request_extension.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button
                :disabled="$v.form_request_extension.$error"
                type="submit"
                ref="submit_request_extension"
              >
                Send
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="alternative-options"
      :width="1050"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('alternative-options')" class="popup__close" type="button"></button>
        <div class="popup__title">Alternative options</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckAltOptForm">
            <h3 class="title reservation-grid__title">Room configuration</h3>
            <div class="reservation-grid__row reservation-grid__row--mixed">
              <app-form-group label="Check-in" class="form-group--no-indent">
                <app-date-picker @input="onSetCheckIn" v-model="form_alternative_options.check_in" placeholder="Check-in" />
              </app-form-group>
              <app-form-group label="Check-out" class="form-group--no-indent">
                <app-date-picker
                  v-model="form_alternative_options.check_out"
                  :readonly="!form_alternative_options.check_in"
                  :default-value="form_alternative_options.check_in"
                  placeholder="Check-out"
                  ref="check_out"
                />
              </app-form-group>
              <app-form-group label="Nights" class="form-group--no-indent" disabled>
                <app-input :value="calcNumberOfNightsOnAltOpt" placeholder="0" disabled />
              </app-form-group>
              <app-form-group label="Meal plan" class="form-group--no-indent">
                <app-select
                  v-model="form_alternative_options.meal_plan"
                  :options="meal_plans_options"
                  :clearable="false"
                  placeholder="Meal plan"
                  :readonly="!form_alternative_options.check_out"
                />
              </app-form-group>
              <app-form-group label="Room" class="form-group--no-indent">
                <app-select
                  v-model="form_alternative_options.type_of_rooms"
                  :options="rooms_options"
                  :clearable="false"
                  placeholder="Choose rooms"
                  :readonly="!form_alternative_options.meal_plan"
                />
              </app-form-group>
            </div>
            <div class="reservation-grid__row reservation-grid__row--times">
              <app-checkbox
                v-model="form_alternative_options.early_check_in"
                label="Early"
              />
              <app-form-group
                label="Check-in time"
                label-for="early_check_in_time"
                class="form-group--no-indent"
                label_left
                :disabled="!form_alternative_options.early_check_in"
              >
                <app-input
                  v-model="form_alternative_options.early_check_in_time"
                  v-mask="'99:99'"
                  placeholder="--:--"
                  autocomplete="off"
                  id="early_check_in_time"
                  name="early_check_in_time"
                  @paste.native.prevent
                  :disabled="!form_alternative_options.early_check_in"
                  class="input--65"
                />
              </app-form-group>
              <app-checkbox
                v-model="form_alternative_options.late_check_out"
                label="Late"
              />
              <app-form-group
                label="Check-out time"
                label-for="late_check_out_time"
                class="form-group--no-indent"
                required
                label_left
                :disabled="!form_alternative_options.late_check_out"
              >
                <app-input
                  v-model="form_alternative_options.late_check_out_time"
                  v-mask="'99:99'"
                  placeholder="--:--"
                  autocomplete="off"
                  id="late_check_out_time"
                  name="late_check_out_time"
                  @paste.native.prevent
                  :disabled="!form_alternative_options.late_check_out"
                  class="input--65"
                />
              </app-form-group>
            </div>
            <div class="reservation-grid__rooms">
              <div
                v-if="form_alternative_options.type_of_rooms && form_alternative_options.type_of_rooms.accomodation_types && form_alternative_options.type_of_rooms.accomodation_types.length"
                class="reservation-grid__types-list"
              >
                <app-room-icon
                  @click="onAltRoomIconClick(type)"
                  v-for="(type, index) in form_alternative_options.type_of_rooms.accomodation_types"
                  :key="index"
                  :accomodation_type="type"
                />
              </div>
            </div>
            <h3 class="title reservation-grid__title">Rooms</h3>
            <template v-if="form_alternative_options.reservation_rooms.length">
              <div class="reservation-grid__room-list">
                <div v-for="(room, index) in form_alternative_options.reservation_rooms" :key="index" class="reservation-grid__room">
                  <AppHotelCard
                    @onCardDelete="onRoomDelete(index, 'form_alternative_options')"
                    :order_number="index + 1"
                    :name="room.hotel_room.name"
                    :accomodation_type="room.accomodation_type"
                    :check_in="room.check_in"
                    :check_out="room.check_out"
                    :mp="room.meal_plan.name"
                    :early="room.early_check_in_time"
                    :late="room.late_check_out_time"
                  />
                </div>
              </div>
            </template>
            <div v-else class="no-results no-results--small no-results--margin">
              <div class="no-results__title">No Results</div>
              <div class="no-results__text">There’s no rooms yet</div>
            </div>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="alternative_options_submit" :disabled="$v.form_alternative_options.$error">
                Add
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getReservationsSalesStatuses,
  patchReservationsHotel,
  postReservationsHotelsAlternativeOption,
  postReservationsHotelsEmailExtendProvisional
} from '@/http'
import { helpers, required, requiredIf } from 'vuelidate/lib/validators'
import AppColoredNotes from '@/components/AppColoredNotes.vue'
import AppRoomIcon from '@/components/AppRoomIcon.vue'
import AppHotelCard from '@/components/AppHotelCard.vue'
const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9])?$/i)

export default {
  name: 'HotelsReservationsGeneral',
  components: { AppHotelCard, AppRoomIcon, AppColoredNotes },
  props: {
    reservation: {
      type: Object,
    },
    rooms_options: {
      type: Array
    },
  },
  data() {
    return {
      form: { rooms: [] },
      meal_plans_options: [],
      id_for_delete: null,
      statuses_options: [],
      reservation_notes: '',
      abnormal: {},
      abnormal_loader: false,
      form_request_extension: {},
      form_alternative_options: { reservation_rooms: [] },
    }
  },
  validations: {
    form_request_extension: {
      date: { required }
    },
    form_alternative_options: {
      check_in: {
        minValue(val) {
          return val ? new Date(val) >= new Date(new Date().setHours(0, 0, 0, 0)) : true
        }
      },
      check_out: {
        minValue(val) {
          return val ? new Date(val).setHours(0, 0, 0, 0) > new Date(new Date(this.form_alternative_options.check_in).setHours(0, 0, 0, 0)) : true
        }
      },
      early_check_in_time: {
        required: requiredIf(function() {
          return this.reservation.early_check_in
        }),
        timeRegex
      },
      late_check_out_time: {
        required: requiredIf(function() {
          return this.reservation.late_check_out
        }),
        timeRegex
      },
    },
  },
  computed: {
    is_reservations_department() {
      return this.$store.state.userGroups.map(item => item.code).includes('reservation_manager')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_assistant')
        || this.$store.state.userGroups.map(item => item.code).includes('reservation_supervisor')
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    calcNumberOfNightsOnAltOpt() {
      if (this.form_alternative_options.check_in && this.form_alternative_options.check_out) {
        const date1 = new Date(this.form_alternative_options.check_in)
        const date2 = new Date(this.form_alternative_options.check_out)
        const timeDiff = date2.getTime() - date1.getTime()
        return Math.ceil(timeDiff / (1000 * 3600 * 24)) < 0 ? 0 : Math.ceil(timeDiff / (1000 * 3600 * 24))
      } else {
        return ''
      }
    },
  },
  created() {
    getReservationsSalesStatuses().then(res => { this.statuses_options = res.data })
  },
  methods: {
    onCheckAltOptForm() {
      this.$v.form_alternative_options.$touch()
      if (this.$v.form_alternative_options.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendAltOptForm()
      }
    },
    sendAltOptForm() {
      this.$refs.alternative_options_submit.preload = true
      postReservationsHotelsAlternativeOption(this.$route.params.id, this.normalizeAltForm())
        .finally(() => { this.$refs.alternative_options_submit.preload = false })
        .then(() => {
          this.getAltOptionsList()
          this.$emit('get-reservation-data')
          this.$notify({
            type: 'success',
            title: 'Alternative options has been added',
          })
          this.$modal.hide('alternative-options')
        })
    },
    normalizeAltForm() {
      const form = {}

      form.hotel = this.reservation.hotel.id
      form.rooms = this.form_alternative_options.reservation_rooms.map(item => {
        return {
          hotel_room: item.hotel_room.id,
          accomodation_type: item.accomodation_type.code,
          early_check_in_time: item.early_check_in ? item.early_check_in_time : null,
          late_check_out_time: item.late_check_out ? item.late_check_out_time : null,
          meal_plan: item.meal_plan.code,
          check_in: item.check_in,
          check_out: item.check_out,
        }
      })

      return form
    },
    onRoomDelete(index, form) {
      this[form].reservation_rooms.splice(index, 1)
    },
    onAltRoomIconClick(type) {
      this.form_alternative_options.reservation_rooms.push({ ...this.form_alternative_options, hotel_room: this.form_alternative_options.type_of_rooms, meal_plan: this.form_alternative_options.meal_plan, accomodation_type: type })
    },
    onSetCheckIn() {
      this.$refs.check_out.$children[0].focus()
    },
    onShowAlternativeOptionsModal() {
      this.form_alternative_options = {
        reservation_rooms: [ ...this.reservation.reservation_rooms ],
        check_in: this.reservation.check_in,
        check_out: this.reservation.check_out,
      }
      this.meal_plans_options = this.reservation.hotel.meal_plans
      this.$modal.show('alternative-options')
    },
    onCheckRequestExtensionForm() {
      this.$v.form_request_extension.$touch()
      if (this.$v.form_request_extension.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendRequestExtensionForm()
      }
    },
    sendRequestExtensionForm() {
      this.$refs.submit_request_extension.preload = true
      postReservationsHotelsEmailExtendProvisional(this.$route.params.id, this.form_request_extension)
        .finally(() => { this.$refs.submit_request_extension.preload = false })
        .then(() => {
          this.$emit('get-reservation-data')
          this.$modal.hide('request_extension_modal')
          this.$notify({
            type: 'success',
            title: 'Request send',
          })
        })
    },
    onRequestExtensionPopupOpen() {
      this.form_request_extension = { date: this.reservation.reservation_deadline }
      this.$modal.show('request_extension_modal')
    },
    onPatchReservationNotes() {
      patchReservationsHotel(this.$route.params.id, { reservation_notes: this.reservation.reservation_notes })
    },
    onReservationPatch() {
      this.$refs.save_other_options.preload = true
      patchReservationsHotel(
        this.$route.params.id,
        {
          reservation_deadline: this.reservation.reservation_deadline,
          voucher: this.reservation.voucher && this.reservation.voucher.id ? this.reservation.voucher.id : null,
        }
      )
        .finally(() => { this.$refs.save_other_options.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$emit('get-reservation-data')
        })
    },
  }
}
</script>
<style lang="sass" scoped>
.reservation-grid__row
  display: grid
  gap: 20px
  margin-bottom: 20px

  &:last-child
    margin-bottom: 0

.reservation-grid__main
  padding-bottom: 20px
  border-bottom: 1px solid #ebebeb

.reservation-grid__row--top
  grid-template-columns: 1fr 140px 140px 65px

.reservation-grid__row--quartet
  grid-template-columns: 125px 1fr 125px 178px
  align-items: center

.reservation-grid__row--single
  grid-template-columns: 206px 100px

.reservation-grid__row--double
  grid-template-columns: repeat(2, 1fr)

.reservation-grid__row--mixed
  grid-template-columns: 150px 150px 80px 160px 1fr

.reservation-grid__row--times
  grid-template-columns: 60px 200px 60px 200px
  align-items: center
  gap: 0 15px

.reservation-grid__room-list
  display: grid
  grid-template-columns: repeat(2, 1fr)
  gap: 0 15px
  margin-bottom: 20px

.reservation-grid__rooms
  margin-bottom: 20px

.reservation-grid__types-list
  display: flex
  justify-content: flex-start
  align-items: center
  flex-wrap: wrap
  gap: 10px

.reservation-grid__room
  margin-bottom: 15px

.reservation-grid__title
  margin-bottom: 15px

.abnormal-wrapper
  max-width: 1080px
</style>